import React from "react";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import {
  ShoppingBagOutlinedIcon,
  RestaurantIcon,
  ClearIcon,
  CheckIcon,
} from "../components/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  selectStatefulCosts,
  setInLocoAnswer,
  setFormAnswer,
  setIdentification,
  clearCheckoutTotem,
} from "../ducks/checkoutSlice";
import CheckoutServerError from "./CheckoutServerError";
import documentDisplay from "../utils/documentDisplay";
import toPriceDisplay from "../utils/toPriceDisplay";
import useGoTo from "../hooks/useGoTo";
import { resetSelfcheckoutSession } from "../ducks/selfcheckoutSession";
import ComandaScan from "./Selfcheckout/ComandaScan";
import { ChoiceStep, KeyboardStep } from "./Selfcheckout/TotemStep";

const STEP_TakingHome = "STEP_TakingHome";
const STEP_PersonalData = "STEP_PersonalData";
const STEP_CPFQuestion = "STEP_CPFQuestion";
const STEP_CPFNumber = "STEP_CPFNumber";
const STEP_Table = "STEP_Table";
const STEP_Comanda = "STEP_Comanda";

function TakingHome({ step, moveStep }) {
  const dispatch = useDispatch();

  if (step !== STEP_TakingHome) return null;

  const setTakingHome = (value) => {
    dispatch(
      setInLocoAnswer({
        name: "isTakingHome",
        value,
      }),
    );
    moveStep(1);
  };

  return (
    <ChoiceStep
      title="Vai comer aqui ou levar?"
      onBack={() => moveStep(false)}
      options={[
        {
          label: "Vou comer aqui",
          icon: <RestaurantIcon />,
          onClick: () => setTakingHome(false),
        },
        {
          label: "Embalar para viagem",
          icon: <ShoppingBagOutlinedIcon />,
          onClick: () => setTakingHome(true),
        },
      ]}
    />
  );
}

function PersonalData({ step, moveStep }) {
  const dispatch = useDispatch();
  const { name } = useSelector((state) => state.checkout);

  if (step !== STEP_PersonalData) return null;

  const nameError =
    name.length > 0 && name.length < 2 ? "Mínimo 2 letras" : null;

  const onKeyboardChange = (input) => {
    dispatch(
      setFormAnswer({
        name: "name",
        value: input.trimStart(),
      }),
    );
  };

  return (
    <KeyboardStep
      title="Como gostaria de ser chamado?"
      value={name}
      placeholder="Seu nome"
      onChange={onKeyboardChange}
      onBack={() => {
        onKeyboardChange("");
        moveStep(false);
      }}
      onConfirm={() => moveStep(1)}
      disabled={name.length < 2}
      error={nameError}
    />
  );
}

function CPFQuestion({ step, moveStep }) {
  const dispatch = useDispatch();

  if (step !== STEP_CPFQuestion) return null;

  const onIdentificationEnableChange = (value) => {
    dispatch(
      setFormAnswer({
        name: "isIdentificationEnable",
        value,
      }),
    );
    moveStep(value ? 1 : 2);
  };

  return (
    <ChoiceStep
      title="CPF ou CNPJ na nota?"
      onBack={() => moveStep(false)}
      options={[
        {
          label: "Não",
          icon: <ClearIcon />,
          onClick: () => onIdentificationEnableChange(false),
        },
        {
          label: "Sim",
          icon: <CheckIcon />,
          onClick: () => onIdentificationEnableChange(true),
        },
      ]}
    />
  );
}

function CPFNumber({ step, moveStep }) {
  const dispatch = useDispatch();
  const { identification, identificationError } = useSelector(
    (state) => state.checkout,
  );

  if (step !== STEP_CPFNumber) return null;

  return (
    <KeyboardStep
      title="Digite o CPF ou CNPJ"
      value={identification ? documentDisplay(identification) : ""}
      placeholder="Só os números"
      keyboard="numeric"
      onChange={(input) => dispatch(setIdentification(input))}
      onBack={() => {
        dispatch(setIdentification(""));
        moveStep(false);
      }}
      onConfirm={() => moveStep(1)}
      disabled={!identification || !!identificationError}
      error={identificationError}
    />
  );
}

function Table({ step, moveStep }) {
  const dispatch = useDispatch();
  const { inLocoLocation } = useSelector((state) => state.checkout);

  if (step !== STEP_Table) return null;

  const nameError =
    inLocoLocation.length > 0 && inLocoLocation.length < 2
      ? "Mínimo 2 letras"
      : null;

  const onKeyboardChange = (input) => {
    dispatch(
      setFormAnswer({
        name: "inLocoLocation",
        value: input.trimStart(),
      }),
    );
  };

  return (
    <KeyboardStep
      title="Onde você está sentado?"
      value={inLocoLocation}
      placeholder="Exemplo: Mesa 100, Balcão"
      onChange={onKeyboardChange}
      onBack={() => {
        onKeyboardChange("");
        moveStep(false);
      }}
      onConfirm={() => moveStep(1)}
      disabled={inLocoLocation.length < 2}
      error={nameError}
    />
  );
}

/**
 * Fim do checkout do autoatendimento (totem, tablet e POS): o rodapé com
 * Cancelar tudo e Finalizar pedido e, se a loja pedir, os passos em tela cheia (comanda, mesa, levar,
 * nome, CPF) antes de enviar.
 */
export default function QuestionCheckout({
  comanda,
  table,
  takingHome,
  personalData,
  cpfQuestion,
  dispatchSubmitCheckout,
}) {
  const goTo = useGoTo();
  const dispatch = useDispatch();
  const { isSubmitting } = useSelector((state) => state.checkout);
  const costs = useSelector(selectStatefulCosts);
  const [step, setStep] = React.useState("");
  const allSteps = [];

  if (comanda) {
    allSteps.push(STEP_Comanda);
  }

  if (table) {
    allSteps.push(STEP_Table);
  }

  if (takingHome) {
    allSteps.push(STEP_TakingHome);
  }

  if (personalData) {
    allSteps.push(STEP_PersonalData);
  }

  if (cpfQuestion) {
    allSteps.push(STEP_CPFQuestion);
    allSteps.push(STEP_CPFNumber);
  }

  const startSteps = () => {
    if (allSteps.length === 0) {
      dispatchSubmitCheckout();
      return;
    }

    setStep(allSteps[0]);
  };

  const moveStep = (next) => {
    const currentIndex = allSteps.indexOf(step);
    if (next === false) {
      if (currentIndex === 0) {
        dispatch(clearCheckoutTotem());
        setStep("");
        return;
      }

      setStep(allSteps[currentIndex - 1]);
      return;
    }

    if (next === 1 || next === 2) {
      const nextIndex = currentIndex + next;
      if (nextIndex === allSteps.length) {
        setStep("");
        dispatchSubmitCheckout();
        return;
      }

      setStep(allSteps[nextIndex]);
      return;
    }
  };

  const onCancel = () => {
    dispatch(resetSelfcheckoutSession());
    goTo("/");
  };

  if (isSubmitting) {
    return (
      <Modal
        show={true}
        backdrop="static"
        dialogClassName="totem-fullscreen-dialog"
        contentClassName="totem-fullscreen-dialog__content"
      >
        <div className="totem-sending">
          <Spinner animation="border" className="spinner-shop" />
          <p className="totem-sending__label">Enviando pedido...</p>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <footer className="totem-footer totem-footer--checkout">
        <button
          type="button"
          className="totem-btn totem-btn--secondary"
          onClick={onCancel}
        >
          Cancelar tudo
        </button>
        <button
          type="button"
          className="btn btn-shop totem-btn totem-btn--primary"
          onClick={startSteps}
        >
          <span className="totem-btn__label">Finalizar pedido</span>
          <span className="totem-btn__price">
            {toPriceDisplay(costs.total)}
          </span>
        </button>
      </footer>
      <Modal
        show={!!step}
        backdrop="static"
        dialogClassName="totem-fullscreen-dialog"
        contentClassName="totem-fullscreen-dialog__content"
      >
        <ComandaScan step={step} moveStep={moveStep} />
        <Table step={step} moveStep={moveStep} />
        <TakingHome step={step} moveStep={moveStep} />
        <PersonalData step={step} moveStep={moveStep} />
        <CPFQuestion step={step} moveStep={moveStep} />
        <CPFNumber step={step} moveStep={moveStep} />
      </Modal>
      <CheckoutServerError />
    </>
  );
}
